import React from "react";
import { Box } from "@mui/material";
import {
  CustomSectionTitle,
  CustomTextInput,
  CustomAutocomplete,
  CustomThreeColInputGroup,
  PostalCodeAutoAddressInput,
  FixedActionBar,
  CustomButton,
  PropertyRegistrationStepper,
  DocumentUploadAccordion,
  CustomSlider,
  CustomFileUploader,
  CustomFormNavigationGuard,
  CustomCategorySelectSingle,
  CustomSingleSelect,
  useScrollToFirstError,
  type CategoryOption,
} from "@ippon/shared-components";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getEmployeeID } from "../../../utils/authUtils";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchDocumentManagement } from "../../../store/documentManagementSlice";
import { createProperty } from "../../../store/propertySlice";
import {
  fetchGroupOptionManagement,
  upsertGroupOptionManagement,
} from "../../../store/groupOptionManagementSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { LabelHelpTooltip } from "@/common/LabelHelpTooltip";
import { propertyDocumentGroups } from "../../../constants/documentGroups";
import LotBoundaryManager from "../../../components/PropertyDetail/LotBoundaryManager";
import { processFileArray } from "../../../utils/fileUpload";
import { reverseLookupPostalCode } from "@/utils/postalCodeLookup";
import { shouldPreventImplicitFormSubmit } from "./formKeyboard";
// プロパティのコンポーネントを使用（存在しないカスタムコンポーネントの代わりに基本的なコンポーネントを使用）

// UUIDv4を生成するヘルパー関数
const generateUUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

type ManualFormValues = {
  type?: string;
  property_category?: string;
  name: string;
  group_name?: string;
  information_source: string;
  postal_code: string;
  prefecture: string;
  city: string;
  block_number: string;
  price: string;
  remarks: string;
  image_urls?: File[];
  // 土地情報
  land_category?: string;
  current_condition?: string;
  land_rights?: string;
  boundary_confirmed?: string;
  land_area_registered?: number;
  land_area_measured?: number;
  lots?: Array<{
    lotNumber: string;
    boundaries: Array<{
      adjacentLand: string;
      confirmation: any[];
      encroachment: string;
      encroachmentMemo: any[];
    }>;
  }>;
  roads?: Array<{
    direction: string;
    boundary_confirmed?: string;
    width: string;
  }>;
  // 建物情報
  house_registration_number?: string;
  building_type?: string;
  structure?: string;
  basement_floors?: number;
  ground_floors?: number;
  building_area?: number;
  private_area?: number;
  total_floor_area?: number;
  total_floor_area_measured?: number;
  built_date?: string;
  inspection_certificate?: string;
  environmental_certification?: string;
  // 設備情報
  equipment_electricity?: string;
  equipment_water_supply?: string;
  equipment_sewerage?: string;
  equipment_city_gas?: string;
  // 都市計画情報
  usage_zone?: string;
  fire_prevention?: string;
  height_district?: string;
  building_coverage_ratio?: string;
  floor_area_ratio?: string;
  // 書類（31種類）
  kozu?: File[];
  land_registry?: File[];
  building_registry?: File[];
  survey_map?: File[];
  boundary_confirmation_docs?: File[];
  encroachment_memo_docs?: File[];
  construction_notice?: File[];
  inspection_certificate_docs?: File[];
  completion_drawing?: File[];
  structural_calculation?: File[];
  sales_contract?: File[];
  important_matters?: File[];
  owner_change_notice?: File[];
  appraisal_report?: File[];
  engineering_report?: File[];
  property_management_contract?: File[];
  building_management_contract?: File[];
  electricity_contract?: File[];
  industrial_waste_contract?: File[];
  general_waste_contract?: File[];
  electrical_equipment_contract?: File[];
  fire_prevention_plan?: File[];
  overall_fire_prevention_plan?: File[];
  chief_fire_manager_appointment?: File[];
  fire_manager_appointment?: File[];
  specific_building_change?: File[];
  fire_manager_delegation?: File[];
  insurance_certificate_docs?: File[];
  trust_contract?: File[];
  trust_certificate?: File[];
  trust_transfer_consent?: File[];
};

const prefectureOptions = [
  { label: "北海道", value: "北海道" },
  { label: "青森県", value: "青森県" },
  { label: "岩手県", value: "岩手県" },
  { label: "宮城県", value: "宮城県" },
  { label: "秋田県", value: "秋田県" },
  { label: "山形県", value: "山形県" },
  { label: "福島県", value: "福島県" },
  { label: "茨城県", value: "茨城県" },
  { label: "栃木県", value: "栃木県" },
  { label: "群馬県", value: "群馬県" },
  { label: "埼玉県", value: "埼玉県" },
  { label: "千葉県", value: "千葉県" },
  { label: "東京都", value: "東京都" },
  { label: "神奈川県", value: "神奈川県" },
  { label: "新潟県", value: "新潟県" },
  { label: "富山県", value: "富山県" },
  { label: "石川県", value: "石川県" },
  { label: "福井県", value: "福井県" },
  { label: "山梨県", value: "山梨県" },
  { label: "長野県", value: "長野県" },
  { label: "岐阜県", value: "岐阜県" },
  { label: "静岡県", value: "静岡県" },
  { label: "愛知県", value: "愛知県" },
  { label: "三重県", value: "三重県" },
  { label: "滋賀県", value: "滋賀県" },
  { label: "京都府", value: "京都府" },
  { label: "大阪府", value: "大阪府" },
  { label: "兵庫県", value: "兵庫県" },
  { label: "奈良県", value: "奈良県" },
  { label: "和歌山県", value: "和歌山県" },
  { label: "鳥取県", value: "鳥取県" },
  { label: "島根県", value: "島根県" },
  { label: "岡山県", value: "岡山県" },
  { label: "広島県", value: "広島県" },
  { label: "山口県", value: "山口県" },
  { label: "徳島県", value: "徳島県" },
  { label: "香川県", value: "香川県" },
  { label: "愛媛県", value: "愛媛県" },
  { label: "高知県", value: "高知県" },
  { label: "福岡県", value: "福岡県" },
  { label: "佐賀県", value: "佐賀県" },
  { label: "長崎県", value: "長崎県" },
  { label: "熊本県", value: "熊本県" },
  { label: "大分県", value: "大分県" },
  { label: "宮崎県", value: "宮崎県" },
  { label: "鹿児島県", value: "鹿児島県" },
  { label: "沖縄県", value: "沖縄県" },
];

// 都市計画系のオプション定義
const usageZoneOptions = [
  { label: "無指定", value: "無指定" },
  { label: "第一種低層住居専用地域", value: "第一種低層住居専用地域" },
  { label: "第二種低層住居専用地域", value: "第二種低層住居専用地域" },
  { label: "第一種中高層住居専用地域", value: "第一種中高層住居専用地域" },
  { label: "第二種中高層住居専用地域", value: "第二種中高層住居専用地域" },
  { label: "第一種住居地域", value: "第一種住居地域" },
  { label: "第二種住居地域", value: "第二種住居地域" },
  { label: "準住居地域", value: "準住居地域" },
  { label: "近隣商業地域", value: "近隣商業地域" },
  { label: "商業地域", value: "商業地域" },
  { label: "準工業地域", value: "準工業地域" },
  { label: "工業地域", value: "工業地域" },
  { label: "工業専用地域", value: "工業専用地域" },
];

const firePreventionOptions = [
  { label: "無指定", value: "無指定" },
  { label: "防火地域", value: "防火地域" },
  { label: "準防火地域", value: "準防火地域" },
];

const heightDistrictOptions = [
  { label: "無指定", value: "無指定" },
  { label: "高度地区", value: "高度地区" },
];

const buildingCoverageRatioOptions = [
  { label: "30%", value: "30" },
  { label: "40%", value: "40" },
  { label: "50%", value: "50" },
  { label: "60%", value: "60" },
  { label: "70%", value: "70" },
  { label: "80%", value: "80" },
];

const floorAreaRatioOptions = [
  { label: "60%", value: "60" },
  { label: "80%", value: "80" },
  { label: "100%", value: "100" },
  { label: "150%", value: "150" },
  { label: "200%", value: "200" },
  { label: "300%", value: "300" },
  { label: "400%", value: "400" },
];

// 建築・土地関連のオプション定義
const houseRegistrationNumberOptions = [
  { label: "あり", value: "あり" },
  { label: "なし", value: "なし" },
];

const buildingTypeOptions = [
  { label: "戸建住宅", value: "戸建住宅" },
  { label: "共同住宅", value: "共同住宅" },
  { label: "店舗・事務所", value: "店舗・事務所" },
  { label: "工場・倉庫", value: "工場・倉庫" },
  { label: "その他", value: "その他" },
];

const structureOptions = [
  { label: "木造", value: "木造" },
  { label: "鉄骨造", value: "鉄骨造" },
  { label: "RC造", value: "RC造" },
  { label: "SRC造", value: "SRC造" },
  { label: "鉄筋コンクリート造", value: "鉄筋コンクリート造" },
];

const propertyCategoryOptions = [
  { label: "レジデンス", value: "residence" },
  { label: "ビルディング", value: "building" },
];

const landCategoryOptions = [
  { label: "宅地", value: "宅地" },
  { label: "田", value: "田" },
  { label: "畑", value: "畑" },
  { label: "山林", value: "山林" },
  { label: "雑種地", value: "雑種地" },
];

const currentConditionOptions = [
  { label: "更地", value: "更地" },
  { label: "建物あり", value: "建物あり" },
  { label: "耕作中", value: "耕作中" },
  { label: "山林", value: "山林" },
];

const landRightsOptions = [
  { label: "所有権", value: "所有権" },
  { label: "信託受益権", value: "信託受益権" },
  { label: "借地権", value: "借地権" },
  { label: "定期借地権", value: "定期借地権" },
  { label: "所有権＋一部借地", value: "所有権＋一部借地" },
  { label: "信託受益権＋一部借地", value: "信託受益権＋一部借地" },
];

const boundaryConfirmedOptions = [
  { label: "確定済み", value: "確定済み" },
  { label: "未確定", value: "未確定" },
];

//

const PropertiesManualCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const leftInputContainerWidth = "600px";
  const labelWidth = "120px";
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  // ステップの状態管理
  const [currentStep, setCurrentStep] = React.useState(1);
  const [prevStep, setPrevStep] = React.useState(1);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [animationDirection, setAnimationDirection] = React.useState<
    "next" | "prev"
  >("next");

  // ステップの完了状態を動的に計算
  const steps = [
    { id: 1, label: "基本情報", completed: currentStep > 1 },
    { id: 2, label: "物件詳細", completed: currentStep > 2 },
    { id: 3, label: "書類", completed: false },
  ];

  // ステップクリック時の処理
  const handleStepClick = (stepId: number) => {
    if (stepId === currentStep || isAnimating) return;

    const direction = stepId > currentStep ? "next" : "prev";
    setPrevStep(currentStep);
    setAnimationDirection(direction);
    setIsAnimating(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setCurrentStep(stepId);
        setTimeout(() => {
          setIsAnimating(false);
        }, 500);
      });
    });
  };

  // 次へボタンのハンドラー
  const handleNext = () => {
    if (currentStep < 3 && !isAnimating) {
      setPrevStep(currentStep);
      setAnimationDirection("next");
      setIsAnimating(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCurrentStep(currentStep + 1);
          setTimeout(() => {
            setIsAnimating(false);
          }, 500);
        });
      });
    }
  };

  // 戻るボタンのハンドラー
  const handleBack = () => {
    if (currentStep === 1) {
      // ステップ1（最初のステップ）の場合は元の戻る
      navigate(-1);
    } else if (!isAnimating) {
      // ステップ2以降は前のステップへ
      setPrevStep(currentStep);
      setAnimationDirection("prev");
      setIsAnimating(true);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCurrentStep(currentStep - 1);
          setTimeout(() => {
            setIsAnimating(false);
          }, 500);
        });
      });
    }
  };

  // 地番と境界の行管理
  const [lots, setLots] = React.useState<
    Array<{
      lotNumber: string;
      boundaries: Array<{
        adjacentLand: string;
        confirmation: any[];
        encroachment: string;
        encroachmentMemo: any[];
      }>;
    }>
  >([]);

  // 接道の行管理
  const [roads, setRoads] = React.useState<
    Array<{ direction: string; boundary_confirmed?: string; width: string }>
  >([{ direction: "", boundary_confirmed: "", width: "" }]);

  const addRoad = () =>
    setRoads((prev) => [
      ...prev,
      { direction: "", boundary_confirmed: "", width: "" },
    ]);
  const removeRoad = (index: number) =>
    setRoads((prev) => prev.filter((_, i) => i !== index));

  // その他書類の管理
  const [otherDocuments, setOtherDocuments] = React.useState<
    Array<{ key: string; label: string }>
  >([]);

  // 「その他」書類の枠定義はクライアント単位 (書類設定 > 物件書類タブ) で管理。
  // 新規作成でも有効な定義を枠として読むだけ (物件ごとの追加・削除はしない)。
  const propertyOtherDocDefs = useSelector(
    (s: RootState) =>
      (s as any).documentManagement?.record?.property_other_documents as
        | Array<{ document_type: string; display_name: string; order: number }>
        | undefined,
  );

  React.useEffect(() => {
    dispatch(fetchDocumentManagement() as any);
  }, [dispatch]);

  React.useEffect(() => {
    setOtherDocuments(
      [...(propertyOtherDocDefs ?? [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((d) => ({ key: d.document_type, label: d.display_name })),
    );
  }, [propertyOtherDocDefs]);

  // 新規作成なので全てのドキュメントキーに空配列を設定（メモ化して無限ループ防止）
  const emptyExistingDocuments = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    propertyDocumentGroups.forEach((group) => {
      group.categories.forEach((category) => {
        map[category.key] = [];
      });
    });
    otherDocuments.forEach((doc) => {
      map[doc.key] = [];
    });
    return map;
  }, [otherDocuments]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    getValues,
    formState: { errors, isDirty },
  } = useForm<ManualFormValues>({
    defaultValues: {
      type: "土地",
      property_category: "",
      name: "",
      group_name: "",
      information_source: "",
      postal_code: "",
      prefecture: "",
      city: "",
      block_number: "",
      price: "",
      remarks: "",
      image_urls: [],
      // 書類フィールド（31種類）
      kozu: [],
      land_registry: [],
      building_registry: [],
      survey_map: [],
      boundary_confirmation_docs: [],
      encroachment_memo_docs: [],
      construction_notice: [],
      inspection_certificate_docs: [],
      completion_drawing: [],
      structural_calculation: [],
      sales_contract: [],
      important_matters: [],
      owner_change_notice: [],
      appraisal_report: [],
      engineering_report: [],
      property_management_contract: [],
      building_management_contract: [],
      electricity_contract: [],
      industrial_waste_contract: [],
      general_waste_contract: [],
      electrical_equipment_contract: [],
      fire_prevention_plan: [],
      overall_fire_prevention_plan: [],
      chief_fire_manager_appointment: [],
      fire_manager_appointment: [],
      specific_building_change: [],
      fire_manager_delegation: [],
      insurance_certificate_docs: [],
      trust_contract: [],
      trust_certificate: [],
      trust_transfer_consent: [],
    },
    mode: "onChange",
  });
  const scrollToFirstError = useScrollToFirstError<ManualFormValues>();

  // ── グループ (予実分析用) ──
  const groupOptionRecord = useSelector(
    (s: RootState) => (s as any).groupOptionManagement.record,
  );
  const [groupOptions, setGroupOptions] = React.useState<CategoryOption[]>([]);
  const groupValue = watch("group_name") || "";

  React.useEffect(() => {
    dispatch(fetchGroupOptionManagement()).catch(() => {});
  }, [dispatch]);

  React.useEffect(() => {
    if (!groupOptionRecord) return;
    const groups: string[] = Array.isArray(groupOptionRecord.groups)
      ? groupOptionRecord.groups
      : [];
    setGroupOptions(groups.map((g) => ({ label: g, value: g })));
  }, [groupOptionRecord]);

  // 「グループ」プルダウンへのユーザー追加はローカルのみ。物件作成が成功した後に
  // まとめて upsertGroupOptionManagement する (修繕の新規モーダルと同じ方針)。
  const handleAddNewGroup = React.useCallback(
    (newGroup: string) => {
      const trimmed = newGroup.trim();
      if (!trimmed) return;
      const exists = groupOptions.some(
        (o) => o.value.toLowerCase() === trimmed.toLowerCase(),
      );
      if (exists) return;
      setGroupOptions((prev) => [...prev, { label: trimmed, value: trimmed }]);
    },
    [groupOptions],
  );

  // フォームの全項目を監視してバリデーション状態を更新
  const watchedFields = watch();
  const watchedImageUrls = watch("image_urls");

  React.useEffect(() => {
    console.log("watchedImageUrls changed:", watchedImageUrls);
  }, [watchedImageUrls]);

  React.useEffect(() => {
    const hasRequiredValues =
      String(watchedFields.name || "").trim() !== "" &&
      String(watchedFields.property_category || "").trim() !== "";
    setIsFormValid(hasRequiredValues);
  }, [watchedFields]);

  const onSubmit = async (data: ManualFormValues) => {
    if (isSubmitting) return;
    console.log("Form data on submit:", data);
    console.log("image_urls on submit:", data.image_urls);
    const employeeId = getEmployeeID();

    if (!data.name) {
      showToast({
        type: "error",
        message: "物件名は必須です",
      });
      return;
    }

    if (!data.property_category) {
      setError("property_category", {
        type: "required",
        message: "種別は必須です",
      });
      showToast({
        type: "error",
        message: "種別は必須です",
      });
      setCurrentStep(1);
      return;
    }

    if (!employeeId) {
      showToast({
        type: "error",
        message: "担当者情報の取得に失敗しました。再ログインしてください。",
      });
      return;
    }

    // 物件IDを事前に生成（Presigned URLのパス生成に必要）
    const propertyId = generateUUID();

    const formData = new FormData();
    // formData.append("type", data.type);
    formData.append("name", data.name);
    if (data.property_category)
      formData.append("property_category", data.property_category);
    if (data.information_source)
      formData.append("information_source", data.information_source);
    formData.append("postal_code", (data.postal_code || "").replace(/-/g, ""));
    formData.append("prefecture", data.prefecture);
    formData.append("city", data.city);
    if (data.block_number) formData.append("block_number", data.block_number);
    // price is required
    // formData.append("price", data.price);
    if (data.remarks) formData.append("remarks", data.remarks);
    if (data.group_name) formData.append("group_name", data.group_name);
    if (employeeId) formData.append("employee_id", employeeId);

    // 土地情報
    if (data.land_category)
      formData.append("land_category", data.land_category);
    if (data.current_condition)
      formData.append("current_condition", data.current_condition);
    if (data.land_rights) formData.append("land_rights", data.land_rights);
    if (data.boundary_confirmed)
      formData.append("boundary_confirmed", data.boundary_confirmed);
    if (
      data.land_area_registered !== undefined &&
      data.land_area_registered !== null &&
      data.land_area_registered !== ("" as unknown as number)
    )
      formData.append(
        "land_area_registered",
        String(data.land_area_registered),
      );
    if (
      data.land_area_measured !== undefined &&
      data.land_area_measured !== null &&
      data.land_area_measured !== ("" as unknown as number)
    )
      formData.append("land_area_measured", String(data.land_area_measured));

    // 地番と境界情報（Presigned URL方式でアップロード）
    if (lots && Array.isArray(lots)) {
      // 空配列の場合は明示的に送信
      if (lots.length === 0) {
        formData.append("lots", JSON.stringify([]));
      }

      for (const [lotIndex, lot] of lots.entries()) {
        // 地番（空でも送信してlotの存在をバックエンドに伝える）
        formData.append(`lots[${lotIndex}][lot_number]`, lot.lotNumber || "");

        // 境界情報
        if (lot.boundaries && Array.isArray(lot.boundaries)) {
          for (const [boundaryIndex, boundary] of lot.boundaries.entries()) {
            // 隣地（空でも送信してboundaryの存在をバックエンドに伝える）
            formData.append(
              `lots[${lotIndex}][boundaries][${boundaryIndex}][adjacent_land]`,
              boundary.adjacentLand || "",
            );
            if (boundary.encroachment) {
              formData.append(
                `lots[${lotIndex}][boundaries][${boundaryIndex}][encroachment]`,
                boundary.encroachment,
              );
            }
            // 確定書類のアップロード（Presigned URL方式）
            if (boundary.confirmation && boundary.confirmation.length > 0) {
              const s3Keys = await processFileArray(
                boundary.confirmation,
                propertyId,
                false,
              );
              if (s3Keys.length > 0) {
                formData.append(
                  `lots[${lotIndex}][boundaries][${boundaryIndex}][confirmation]`,
                  JSON.stringify(s3Keys.map((key) => ({ data: key }))),
                );
              }
            }
            // 越境覚書のアップロード（Presigned URL方式）
            if (
              boundary.encroachmentMemo &&
              boundary.encroachmentMemo.length > 0
            ) {
              const s3Keys = await processFileArray(
                boundary.encroachmentMemo,
                propertyId,
                false,
              );
              if (s3Keys.length > 0) {
                formData.append(
                  `lots[${lotIndex}][boundaries][${boundaryIndex}][encroachment_memo]`,
                  JSON.stringify(s3Keys.map((key) => ({ data: key }))),
                );
              }
            }
          }
        }
      }
    }

    // 接道情報（配列をJSONで送信）
    if (roads && Array.isArray(roads)) {
      const roadsToSend = roads
        .filter((r) => r.direction || r.boundary_confirmed || r.width)
        .map((r) => ({
          direction: r.direction || "",
          boundary_confirmed: r.boundary_confirmed || "",
          width: r.width ? Number(r.width) : undefined,
        }));
      if (roadsToSend.length > 0) {
        formData.append("roads", JSON.stringify(roadsToSend));
      }
    }

    // 建物情報
    if (data.house_registration_number)
      formData.append(
        "house_registration_number",
        data.house_registration_number,
      );
    if (data.building_type)
      formData.append("building_type", data.building_type);
    if (data.structure) formData.append("structure", data.structure);
    if (data.basement_floors !== undefined && data.basement_floors !== null)
      formData.append("basement_floors", String(data.basement_floors));
    if (data.ground_floors !== undefined && data.ground_floors !== null)
      formData.append("ground_floors", String(data.ground_floors));
    if (data.building_area !== undefined && data.building_area !== null)
      formData.append("building_area", String(data.building_area));
    if (data.private_area !== undefined && data.private_area !== null)
      formData.append("private_area", String(data.private_area));
    if (data.total_floor_area !== undefined && data.total_floor_area !== null)
      formData.append("total_floor_area", String(data.total_floor_area));
    if (
      data.total_floor_area_measured !== undefined &&
      data.total_floor_area_measured !== null
    )
      formData.append(
        "total_floor_area_measured",
        String(data.total_floor_area_measured),
      );
    if (data.built_date) formData.append("built_date", data.built_date);
    if (data.inspection_certificate)
      formData.append("inspection_certificate", data.inspection_certificate);
    if (data.environmental_certification)
      formData.append(
        "environmental_certification",
        data.environmental_certification,
      );

    // 設備情報
    if (data.equipment_electricity)
      formData.append("equipment_electricity", data.equipment_electricity);
    if (data.equipment_water_supply)
      formData.append("equipment_water_supply", data.equipment_water_supply);
    if (data.equipment_sewerage)
      formData.append("equipment_sewerage", data.equipment_sewerage);
    if (data.equipment_city_gas)
      formData.append("equipment_city_gas", data.equipment_city_gas);

    // 都市計画情報
    if (data.usage_zone) formData.append("usage_zone", data.usage_zone);
    if (data.fire_prevention)
      formData.append("fire_prevention", data.fire_prevention);
    if (data.height_district)
      formData.append("height_district", data.height_district);
    if (data.building_coverage_ratio)
      formData.append("building_coverage_ratio", data.building_coverage_ratio);
    if (data.floor_area_ratio)
      formData.append("floor_area_ratio", data.floor_area_ratio);

    // 物件画像のアップロード（Presigned URL方式）
    if (
      data.image_urls &&
      Array.isArray(data.image_urls) &&
      data.image_urls.length > 0
    ) {
      const s3Keys = await processFileArray(data.image_urls, propertyId, false);
      if (s3Keys.length > 0) {
        formData.append(
          "image_urls",
          JSON.stringify(s3Keys.map((key) => ({ data: key }))),
        );
      }
    }

    // 書類のアップロード（31種類、Presigned URL方式）
    const documentKeys = [
      "kozu",
      "land_registry",
      "building_registry",
      "survey_map",
      "boundary_confirmation_docs",
      "encroachment_memo_docs",
      "construction_notice",
      "inspection_certificate_docs",
      "completion_drawing",
      "structural_calculation",
      "sales_contract",
      "important_matters",
      "owner_change_notice",
      "appraisal_report",
      "engineering_report",
      "property_management_contract",
      "building_management_contract",
      "electricity_contract",
      "industrial_waste_contract",
      "general_waste_contract",
      "electrical_equipment_contract",
      "fire_prevention_plan",
      "overall_fire_prevention_plan",
      "chief_fire_manager_appointment",
      "fire_manager_appointment",
      "specific_building_change",
      "fire_manager_delegation",
      "insurance_certificate_docs",
      "trust_contract",
      "trust_certificate",
      "trust_transfer_consent",
    ];

    for (const key of documentKeys) {
      const files = (data as any)[key];
      if (files && Array.isArray(files) && files.length > 0) {
        const s3Keys = await processFileArray(files, propertyId, false);
        if (s3Keys.length > 0) {
          formData.append(
            key,
            JSON.stringify(s3Keys.map((key) => ({ data: key }))),
          );
        }
      }
    }

    // その他の書類のアップロード（Presigned URL方式）
    if (otherDocuments.length > 0) {
      // ラベル名のマップを送信
      const otherDocumentsMap: Record<string, string> = {};
      otherDocuments.forEach((doc) => {
        otherDocumentsMap[doc.key] = doc.label;
      });
      formData.append(
        "other_documents_labels",
        JSON.stringify(otherDocumentsMap),
      );

      // 各その他書類のファイルを送信
      for (const doc of otherDocuments) {
        const files = (data as any)[doc.key];
        if (files && Array.isArray(files) && files.length > 0) {
          const s3Keys = await processFileArray(files, propertyId, false);
          if (s3Keys.length > 0) {
            formData.append(
              doc.key,
              JSON.stringify(s3Keys.map((key) => ({ data: key }))),
            );
          }
        }
      }
    }

    try {
      setIsSubmitting(true);
      const resultAction = await dispatch(createProperty(formData));
      if ((createProperty as any).fulfilled.match(resultAction)) {
        // 物件作成成功 → ローカルで追加された新規グループをまとめて upsert する。
        const beGroups: string[] = Array.isArray(groupOptionRecord?.groups)
          ? groupOptionRecord.groups
          : [];
        const localGroupValues = groupOptions.map((o) => o.value);
        const hasNewGroup = localGroupValues.some((v) => !beGroups.includes(v));
        if (hasNewGroup) {
          const merged = Array.from(
            new Set([...beGroups, ...localGroupValues]),
          );
          dispatch(
            upsertGroupOptionManagement({
              existingId: groupOptionRecord?.group_option_management_id,
              groups: merged,
            }),
          ).catch(() => {});
        }

        const payload: any = (resultAction as any).payload;
        const linkId = payload?.id;
        if (linkId) {
          navigate(`/properties/${linkId}`, {
            state: {
              propName: data.name,
              showToast: {
                message: "物件情報の登録に成功しました",
                type: "success",
              },
            },
          });
        } else {
          navigate("/properties", {
            state: {
              showToast: {
                message: "物件情報の登録に成功しました",
                type: "success",
              },
            },
          });
        }
      } else {
        const message =
          (resultAction as any)?.payload?.message ||
          "物件登録中にエラーが発生しました";
        showToast({ type: "error", message });
      }
    } catch (error: any) {
      console.error("物件登録エラー:", error);
      const message =
        error?.message ||
        error?.response?.data?.message ||
        "ファイルのアップロードまたは物件登録に失敗しました";
      showToast({ type: "error", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ステップごとのコンテンツをレンダリング
  const renderStepContent = (step: number) => {
    switch (step) {
      case 1: // 基本情報
        return (
          <Box data-step="1">
            <CustomSectionTitle title="基本情報" size="small" />
            <Box sx={{ mt: 2 }}>
              <CustomTextInput
                label="物件名"
                name="name"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                isRequired
              />
              <CustomSingleSelect
                label="種別"
                name="property_category"
                control={control}
                errors={errors}
                options={propertyCategoryOptions}
                placeholder="選択してください"
                labelWidth={labelWidth}
                inputWidth={leftInputContainerWidth}
                isRequired
              />
              <CustomCategorySelectSingle
                label="グループ"
                labelSuffix={
                  <LabelHelpTooltip title="物件をファンドや管理グループでまとめることができ、予実分析にて各グループごとの予実を確認できます。" />
                }
                name="group_name"
                options={groupOptions}
                selectedValue={groupValue}
                onValueChange={(val) =>
                  setValue("group_name", val, {
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }
                onAddNewOption={handleAddNewGroup}
                labelWidth={labelWidth}
                inputWidth={leftInputContainerWidth}
              />
              <PostalCodeAutoAddressInput
                register={register}
                errors={errors}
                control={control}
                setValue={setValue}
                getValues={getValues}
                setError={setError}
                clearErrors={clearErrors}
                prefectureOptions={prefectureOptions}
                postalCodeFieldName="postal_code"
                prefectureFieldName="prefecture"
                cityFieldName="city"
                blockNumberFieldName="block_number"
                postalCodeLabel="郵便番号"
                prefectureLabel="都道府県"
                cityLabel="市区町村・番地"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                autoSearchEnabled={false}
                showSearchButton={true}
                showReverseSearchButton={true}
                autoReverseSearchEnabled={false}
                reversePostalCodeLookup={reverseLookupPostalCode}
              />
              <CustomTextInput
                label="建物名、部屋番号"
                name="block_number"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
              />
              <Box sx={{ mt: 2 }}>
                <CustomFileUploader
                  name="image_urls"
                  register={register as any}
                  setValue={setValue as any}
                  initialImages={(watch("image_urls") as any) || []}
                  showLabel="true"
                  width="180px"
                  errors={errors}
                  label="物件画像"
                  onImageChange={() => {
                    // CustomFileUploaderが内部でsetValueを呼び出すので、
                    // 次のレンダリングサイクルで値を取得してshouldDirtyを設定
                    setTimeout(() => {
                      const currentImages = getValues("image_urls") || [];
                      console.log(
                        "onImageChange - currentImages:",
                        currentImages,
                      );
                      setValue("image_urls", currentImages, {
                        shouldDirty: true,
                      });
                    }, 0);
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      case 2: // 物件詳細（土地情報 + 建物・設備情報）
        return (
          <Box data-step="2">
            {/* 土地情報 */}
            <CustomSectionTitle title="土地情報" size="small" />
            <Box sx={{ mt: 2, mb: 3 }}>
              <CustomAutocomplete
                label="地目"
                name="land_category"
                placeholder="入力または選択してください"
                control={control}
                options={landCategoryOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="現況"
                name="current_condition"
                placeholder="入力または選択してください"
                control={control}
                options={currentConditionOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="権利関係"
                name="land_rights"
                placeholder="入力または選択してください"
                control={control}
                options={landRightsOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="境界確定"
                name="boundary_confirmed"
                placeholder="入力または選択してください"
                control={control}
                options={boundaryConfirmedOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="住宅面積 公簿"
                name="land_area_registered"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="敷地面積（実測）"
                name="land_area_measured"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
            </Box>

            {/* 地番と境界情報 */}
            <Box sx={{ mb: 2 }}>
              <LotBoundaryManager
                lots={lots}
                onLotsChange={(newLots) => {
                  setLots(newLots);
                  // フォームにも変更を通知してdirtyにする
                  setValue("lots" as any, newLots, { shouldDirty: true });
                }}
              />
            </Box>

            {/* 接道情報 */}
            <Box sx={{ mb: 2, width: { xs: "100%", sm: "600px" } }}>
              {roads.map((road, index) => (
                <CustomThreeColInputGroup
                  key={`road-${index}`}
                  title={index === 0 ? "接道情報" : undefined}
                  firstPlaceholder="例: 南側"
                  secondPlaceholder="選択してください"
                  thirdPlaceholder="例: 10"
                  firstLabel="接道面"
                  secondLabel="境界確定"
                  thirdLabel="道路幅員"
                  secondFieldType="select"
                  secondFieldOptions={[
                    { label: "あり", value: "あり" },
                    { label: "なし", value: "なし" },
                  ]}
                  thirdUnit="m"
                  index={index}
                  onRemove={
                    roads.length > 1 ? () => removeRoad(index) : undefined
                  }
                  values={{
                    first: road.direction,
                    second: road.boundary_confirmed || "",
                    third: road.width,
                  }}
                  onChange={(field, value) => {
                    const newRoads = [...roads];
                    if (field === "first") {
                      newRoads[index].direction = value;
                      setValue(
                        `roads.${index}.direction` as keyof ManualFormValues,
                        value,
                        {
                          shouldDirty: true,
                        },
                      );
                    } else if (field === "second") {
                      newRoads[index].boundary_confirmed = value;
                      setValue(
                        `roads.${index}.boundary_confirmed` as keyof ManualFormValues,
                        value,
                        {
                          shouldDirty: true,
                        },
                      );
                    } else if (field === "third") {
                      newRoads[index].width = value;
                      setValue(
                        `roads.${index}.width` as keyof ManualFormValues,
                        value,
                        {
                          shouldDirty: true,
                        },
                      );
                    }
                    setRoads(newRoads);
                  }}
                  inputWidth="223px"
                />
              ))}
              <Box sx={{ mt: 2 }}>
                <CustomButton
                  label="＋接道を追加"
                  buttonCategory="clearPrimary"
                  onClick={addRoad}
                  sx={{
                    width: { xs: "100%", sm: "600px" },
                    borderStyle: "dashed",
                  }}
                />
              </Box>
            </Box>

            {/* 建物情報 */}
            <CustomSectionTitle title="建物情報" size="small" />
            <Box sx={{ mt: 2, mb: 3 }}>
              <CustomAutocomplete
                label="家屋番号"
                name="house_registration_number"
                placeholder="入力または選択してください"
                control={control}
                options={houseRegistrationNumberOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="建物 種類"
                name="building_type"
                placeholder="入力または選択してください"
                control={control}
                options={buildingTypeOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="構造"
                name="structure"
                placeholder="入力または選択してください"
                control={control}
                options={structureOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="地下(階)"
                name="basement_floors"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="地上(階)"
                name="ground_floors"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="建築面積"
                name="building_area"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="専有面積"
                name="private_area"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="延床面積（公簿）"
                name="total_floor_area"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="延床面積（実測）"
                name="total_floor_area_measured"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="竣工年月日"
                name="built_date"
                placeholder="YYYY/MM"
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="検査済証"
                name="inspection_certificate"
                placeholder="入力または選択してください"
                control={control}
                options={houseRegistrationNumberOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="環境認証"
                name="environmental_certification"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
            </Box>

            {/* 設備情報 */}
            <CustomSectionTitle title="設備情報" size="small" />
            <Box sx={{ mt: 2, mb: 3 }}>
              <CustomTextInput
                label="設備 電気"
                name="equipment_electricity"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="設備 上水道"
                name="equipment_water_supply"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="設備 下水道"
                name="equipment_sewerage"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomTextInput
                label="設備 都市ガス"
                name="equipment_city_gas"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
            </Box>

            {/* 都市計画情報 */}
            <CustomSectionTitle title="都市計画情報" size="small" />
            <Box sx={{ mt: 2, mb: 3 }}>
              <CustomAutocomplete
                label="用途地域"
                name="usage_zone"
                placeholder="入力または選択してください"
                control={control}
                options={usageZoneOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="防火指定"
                name="fire_prevention"
                placeholder="入力または選択してください"
                control={control}
                options={firePreventionOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="高度地区"
                name="height_district"
                placeholder="入力または選択してください"
                control={control}
                options={heightDistrictOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="建蔽率(%)"
                name="building_coverage_ratio"
                placeholder="入力または選択してください"
                control={control}
                options={buildingCoverageRatioOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
              <CustomAutocomplete
                label="容積率(%)"
                name="floor_area_ratio"
                placeholder="入力または選択してください"
                control={control}
                options={floorAreaRatioOptions}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
              />
            </Box>

            {/* その他・備考 */}
            <CustomSectionTitle title="その他" size="small" />
            <Box sx={{ mt: 2 }}>
              <CustomTextInput
                label="備考"
                name="remarks"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp="100%"
                multiline={true}
                autoResize={true}
                minRows={3}
                maxRows={20}
              />
            </Box>
          </Box>
        );
      case 3: // 書類
        return (
          <Box data-step="3">
            <CustomSectionTitle title="書類" size="small" />
            <Box sx={{ my: 2, mx: 0.5 }}>
              <DocumentUploadAccordion
                register={register}
                setValue={setValue}
                errors={errors}
                watch={watch}
                documentGroups={propertyDocumentGroups}
                existingDocuments={emptyExistingDocuments}
                otherDocuments={otherDocuments}
                onDownload={(doc) => {
                  if (doc.file) {
                    const url = URL.createObjectURL(doc.file);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = doc.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }
                }}
                onDocumentDeleted={() => {
                  // 新規作成ページなので既存ドキュメントの削除処理は不要
                }}
              />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  // 最終的な送信ボタンの処理
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep === 3) {
      // フロントエンドガード: 物件名が無ければ API を呼ばずに step 1 へ戻して案内する。
      // (RHF の required ルールが name に効かないケースがあるため、明示的にここで弾く)
      const values = getValues();
      if (!values.name?.trim()) {
        setError("name", { type: "required", message: "物件名は必須です" });
        setCurrentStep(1);
        // slider アニメーション (≈500ms) 後に物件名 input へスクロール + フォーカス
        setTimeout(() => {
          const nameEl = document.querySelector<HTMLInputElement>(
            'input[name="name"]:not([type="hidden"])',
          );
          nameEl?.scrollIntoView({ behavior: "smooth", block: "center" });
          nameEl?.focus({ preventScroll: true });
        }, 550);
        return;
      }
      if (!values.property_category?.trim()) {
        setError("property_category", {
          type: "required",
          message: "種別は必須です",
        });
        setCurrentStep(1);
        setTimeout(() => {
          const categoryEl = document.querySelector<HTMLElement>(
            '[data-testid="select-property_category"]',
          );
          categoryEl?.scrollIntoView({ behavior: "smooth", block: "center" });
          categoryEl?.focus({ preventScroll: true });
        }, 550);
        return;
      }
      // それ以外は通常 submit。validation エラー時は該当 step まで戻してからスクロール。
      handleSubmit(onSubmit, (errors, ev) => {
        const formEl =
          (ev?.target as HTMLElement | undefined) instanceof HTMLFormElement
            ? (ev?.target as HTMLFormElement)
            : ((ev?.target as HTMLElement | undefined)?.closest?.("form") ??
              null);
        const firstKey = Object.keys(errors)[0];
        const firstField = firstKey
          ? formEl?.querySelector<HTMLElement>(
              `[name="${firstKey}"], [data-rhf-field="${firstKey}"], [data-testid="select-${firstKey}"]`,
            )
          : null;
        const stepStr =
          firstField?.closest<HTMLElement>("[data-step]")?.dataset.step;
        const stepNum = stepStr ? Number(stepStr) : null;
        if (stepNum && stepNum !== currentStep) {
          setCurrentStep(stepNum);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => scrollToFirstError(errors, ev));
          });
        } else {
          scrollToFirstError(errors, ev);
        }
      })();
    } else {
      // それ以外は次へ進む
      handleNext();
    }
  };

  return (
    <CustomFormNavigationGuard
      hasFormChanged={isDirty}
      isSubmitting={isSubmitting}
    >
      {() => (
        <Box>
          {/* タイトル部分 */}
          <Box pt={{ xs: 2, sm: 3 }}>
            <CustomSectionTitle
              title="物件登録"
              addBorder={false}
              size="large"
            />
          </Box>

          {/* ボーダー */}
          <Box
            sx={{
              borderBottom: "1px solid #E0E0E0",
              ml: -3,
              mr: -3,
            }}
          />

          {/* グレー背景のメインコンテンツエリア */}
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              ml: -3,
              mr: -3,
              p: 2,
              pb: 9,
              minHeight: "calc(100vh - 400px)",
            }}
          >
            <Box
              component="form"
              onSubmit={handleFormSubmit}
              onKeyDown={(event) => {
                const target = event.target as HTMLInputElement;
                if (
                  shouldPreventImplicitFormSubmit({
                    key: event.key,
                    defaultPrevented: event.defaultPrevented,
                    target: { tagName: target.tagName, type: target.type },
                  })
                ) {
                  event.preventDefault();
                }
              }}
              sx={{ minWidth: 0, maxWidth: "100%" }}
            >
              {/* ステッパー（白背景） */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  minWidth: 0,
                }}
              >
                <PropertyRegistrationStepper
                  currentStep={currentStep}
                  steps={steps}
                  onStepClick={handleStepClick}
                />
              </Box>

              {/* フォーム要素（白背景） */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  p: 2,
                  borderRadius: "8px",
                  minHeight: "calc(100vh - 600px)",
                  mt: 1,
                }}
              >
                <CustomSlider
                  currentStep={currentStep - 1}
                  prevStep={prevStep - 1}
                  direction={animationDirection}
                  isAnimating={isAnimating}
                  animationDuration={300}
                  minHeight="300px"
                >
                  {renderStepContent(1)}
                  {renderStepContent(2)}
                  {renderStepContent(3)}
                </CustomSlider>
              </Box>

              {/* 固定アクションバー */}
              <FixedActionBar
                submitLabel={currentStep === 3 ? "登録" : "次へ"}
                isLoading={isSubmitting}
                disabled={currentStep === 3 && (!isFormValid || isSubmitting)}
                showCancel
                cancelLabel="戻る"
                onCancel={handleBack}
              />
            </Box>
          </Box>
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default PropertiesManualCreatePage;
